import { HTMLAttributes, ReactNode } from 'react';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  hover?: boolean;
  glass?: boolean;
  noPadding?: boolean;
}

export default function Card({
  children,
  hover = false,
  glass = false,
  noPadding = false,
  className = '',
  ...rest
}: CardProps) {
  return (
    <div
      className={`
        rounded-xl border
        ${glass
          ? 'glass'
          : 'bg-background-50 border-background-200'}
        ${hover ? 'transition-all duration-200 hover:shadow-raised hover:-translate-y-0.5 cursor-pointer' : 'shadow-card'}
        ${noPadding ? '' : 'p-5'}
        ${className}
      `}
      {...rest}
    >
      {children}
    </div>
  );
}

/* ── Sub-components ──────────────────────────────────────────── */
interface CardHeaderProps {
  title: string;
  subtitle?: string;
  icon?: string;
  iconBg?: string;
  action?: ReactNode;
}

export function CardHeader({ title, subtitle, icon, iconBg = 'bg-primary-100', action }: CardHeaderProps) {
  return (
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        {icon && (
          <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${iconBg}`}>
            <i className={`${icon} text-lg text-primary-700`} />
          </div>
        )}
        <div>
          <h3 className="font-heading font-700 text-foreground-900 text-base">{title}</h3>
          {subtitle && <p className="text-xs text-foreground-500 mt-0.5 font-label">{subtitle}</p>}
        </div>
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

interface CardFooterProps {
  children: ReactNode;
  border?: boolean;
}

export function CardFooter({ children, border = true }: CardFooterProps) {
  return (
    <div className={`mt-4 pt-4 ${border ? 'border-t border-background-200' : ''} flex items-center justify-between`}>
      {children}
    </div>
  );
}